"use client";
import React, { useState } from "react";

import clsx from "clsx";
import Link from "next/link";
import { useParams } from "next/navigation";

import { useTranslation } from "~/app/hooks/useTranslation";
import { buildLocalizedHref } from "~/app/lib/routeUtils";
import Icon1Svg from "~/assets/images/becomeAPartner/icon1.svg?react";
import Icon2Svg from "~/assets/images/becomeAPartner/icon2.svg?react";
import Icon3Svg from "~/assets/images/becomeAPartner/icon3.svg?react";
import { submitFormMessage } from "~/common/api/formMessagesClient";
import Input from "~/common/components/Input";
import Modal from "~/common/components/Modal";
import { isEmailFormatValid, isFreeEmailDomain } from "~/common/utils/email";
import { formatPhoneInput, getDefaultPhoneCountry, isPhoneValid, normalizePhoneNumber } from "~/common/utils/phone";

import styles from "./BecomeAPartnerSection2.module.scss";

function SendMessageForm({ isTechPartner }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    typeOfQuestion: "",
    message: "",
    category: isTechPartner ? "tech" : "sales",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const { locale } = useParams();
  const phoneCountry = getDefaultPhoneCountry(locale);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "phone" ? formatPhoneInput(value, phoneCountry) : value,
    }));
  };

  function validate(values, normalizedPhone) {
    const errs = {};
    if (!values.firstName || values.firstName.trim().length < 2) {
      errs.firstName = t("common.errors.invalidFirstName") || "Invalid";
    }
    if (!values.lastName || values.lastName.trim().length < 2) {
      errs.lastName = t("common.errors.invalidLastName") || "Invalid";
    }
    const email = typeof values.email === "string" ? values.email.trim() : "";
    if (!email || !isEmailFormatValid(email)) {
      errs.email = t("common.errors.invalidEmail") || "Invalid";
    } else if (isFreeEmailDomain(email)) {
      errs.email = t("common.errors.freeEmail") || "Invalid";
    }
    if (!normalizedPhone) {
      errs.phone = t("common.errors.required") || "Required";
    } else if (!isPhoneValid(values.phone, phoneCountry)) {
      errs.phone = t("common.errors.invalidPhone") || "Invalid";
    }
    if (!values.category) {
      errs.category = t("common.errors.required") || "Required";
    }
    if (!values.message || values.message.trim().length < 5) {
      errs.message = t("common.errors.messageTooShort") || "Too short";
    }
    return errs;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const normalizedPhone = normalizePhoneNumber(formData.phone, phoneCountry);
    const vErrors = validate(formData, normalizedPhone);
    setErrors(vErrors);
    if (Object.keys(vErrors).length) return;
    try {
      setSubmitting(true);
      setErrors({});
      const metadata = {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screen: typeof window !== "undefined" ? { w: window.screen.width, h: window.screen.height } : null,
        locale: typeof navigator !== "undefined" ? navigator.language : null,
        context: isTechPartner ? "become-a-tech-partner-form" : "become-a-partner-form",
        appLocale: locale,
        partnershipProgram: isTechPartner ? "technology" : "business",
        referrer: typeof document !== "undefined" ? document.referrer || null : null,
        href: typeof window !== "undefined" ? window.location.href : null,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      };
      await submitFormMessage({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: normalizedPhone,
        category: formData.category,
        message: formData.message.trim(),
        metadata,
      });
      setModalOpen(true);
      setFormData((p) => ({ ...p, message: "" }));
    } catch (err) {
      if (err.validation) setErrors(err.validation);
      else setErrors({ form: err.message || "Error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form id="partner-application" className={styles.form} onSubmit={handleSubmit} noValidate>
      {!isTechPartner && (
        <div className={clsx(styles.row100, styles.formIntro)}>
          <h2>{t("becomeAPartner.referral.form.title")}</h2>
          <p>{t("becomeAPartner.referral.form.description")}</p>
        </div>
      )}
      <div className={styles.row50}>
        <Input
          id="firstName"
          name="firstName"
          label={t("keepInTouch.section2.form.firstName")}
          required
          value={formData.firstName}
          onChange={handleChange}
          error={errors.firstName}
        />
      </div>
      <div className={styles.row50}>
        <Input
          id="lastName"
          name="lastName"
          label={t("keepInTouch.section2.form.lastName")}
          required
          value={formData.lastName}
          onChange={handleChange}
          error={errors.lastName}
        />
      </div>

      <div className={styles.row50}>
        <Input
          id="email"
          name="email"
          type="email"
          label={t("keepInTouch.section2.form.email")}
          required
          value={formData.email}
          onChange={handleChange}
          error={errors.email}
        />
      </div>
      <div className={styles.row50}>
        <Input
          id="phone"
          name="phone"
          type="tel"
          label={isTechPartner ? t("keepInTouch.section2.form.phone") : t("becomeAPartner.referral.form.phone")}
          required
          value={formData.phone}
          onChange={handleChange}
          error={errors.phone}
          inputMode="tel"
          autoComplete="tel"
        />
      </div>
      <div className={styles.row100}>
        <Input
          name="category"
          label={t("becomeAPartner.form.program")}
          options={[
            { value: isTechPartner ? "tech" : "sales", label: isTechPartner ? t("becomeAPartner.form.tech") : t("becomeAPartner.form.sales") },
          ]}
          value={formData.category}
          onChange={handleChange}
          required
          error={errors.category}
        />
      </div>
      <div className={styles.row100}>
        <Input
          id="message"
          name="message"
          label={isTechPartner ? t("keepInTouch.section2.form.yourMessage") : t("becomeAPartner.referral.form.message")}
          helperText={isTechPartner ? "" : t("becomeAPartner.referral.form.messageHint")}
          multiline
          minRows={8}
          maxRows={10}
          required
          value={formData.message}
          onChange={handleChange}
          error={errors.message}
        />
      </div>
      <div className={styles.inputWrapper}>
        <button type="submit" disabled={submitting}>
          {submitting ? t("common.submitting") || "..." : isTechPartner ? t("keepInTouch.section2.form.sendMessage") : t("becomeAPartner.referral.form.submit")}
        </button>
        {errors.form && <p style={{ color: "#c00", fontSize: 12 }}>{errors.form}</p>}

        <p className={styles.terms}>
          {t("keepInTouch.section2.form.termsPart1")}&nbsp;
          <Link href={buildLocalizedHref(locale, "privacy-policy", { tab: "terms-of-use" })}>
            {t("keepInTouch.section2.form.termsOfService")}
          </Link>
          &nbsp;
          {t("keepInTouch.section2.form.termsPart2")}&nbsp;
          <Link href={buildLocalizedHref(locale, "privacy-policy", { tab: "privacy-policy" })}>
            {t("keepInTouch.section2.form.privacyNotice")}
          </Link>
          {locale === "tr" && " "}{t("keepInTouch.section2.form.termsPart3")}
        </p>
      </div>
      <Modal
        idPrefix="form-message-become-partner"
        title={t("common.thankYou")}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        footer={
          <button btnType="default" onClick={() => setModalOpen(false)}>
            {t("common.ok")}
          </button>
        }
      >
        <p>{isTechPartner ? t("common.formSubmitted") : t("becomeAPartner.referral.form.success")}</p>
      </Modal>
    </form>
  );
}

const BecomeAPartnerSection2 = ({ isTechPartner = false }) => {
  const { t } = useTranslation();
  return (
    <div className={clsx(styles.section, "card")}>
      {isTechPartner && <SendMessageForm key="tech" isTechPartner />}

      <div className={styles.content}>
        <h2>{isTechPartner ? t("becomeAPartner.tech.title") : t("becomeAPartner.referral.section2.title")}</h2>
        {!isTechPartner && <p>{t("becomeAPartner.referral.section2.description")}</p>}
      </div>
      <div className={styles.partner}>
        <div className={styles.item}>
          <Icon1Svg />
          <h3>{isTechPartner ? t("becomeAPartner.tech.scope.title") : t("becomeAPartner.referral.section2.icon1.title")}</h3>
          <p>{isTechPartner ? t("becomeAPartner.tech.scope.description") : t("becomeAPartner.referral.section2.icon1.description")}</p>
        </div>
        <div className={styles.item}>
          <Icon2Svg />
          <h3>{isTechPartner ? t("becomeAPartner.tech.build.title") : t("becomeAPartner.referral.section2.icon2.title")}</h3>
          <p>{isTechPartner ? t("becomeAPartner.tech.build.description") : t("becomeAPartner.referral.section2.icon2.description")}</p>
        </div>
        <div className={styles.item}>
          <Icon3Svg />
          <h3>{isTechPartner ? t("becomeAPartner.tech.launch.title") : t("becomeAPartner.referral.section2.icon3.title")}</h3>
          <p>{isTechPartner ? t("becomeAPartner.tech.launch.description") : t("becomeAPartner.referral.section2.icon3.description")}</p>
        </div>
      </div>
      {!isTechPartner && <SendMessageForm key="business" isTechPartner={false} />}
    </div>
  );
};

export default BecomeAPartnerSection2;

